import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Retirement = () => {
  // State variables
  const [currentSavings, setCurrentSavings] = useState('');
  const [annualContributions, setAnnualContributions] = useState('');
  const [annualReturnRate, setAnnualReturnRate] = useState('');
  const [years, setYears] = useState('');
  const [futureRetirementSavings, setFutureRetirementSavings] = useState(0);

  // Recalculate future retirement savings whenever any of the inputs change
  useEffect(() => {

    if (currentSavings && annualContributions && annualReturnRate && years) {
      // Formula for compound savings goal for retirement
      const r = annualReturnRate / 100; // Annual return rate
      const futureSavings =
        currentSavings * Math.pow(1 + r, years) +
        annualContributions * ((Math.pow(1 + r, years) - 1) / r);
      setFutureRetirementSavings(futureSavings);
    } else {
      setFutureRetirementSavings(0); // In case of incomplete input
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [currentSavings, annualContributions, annualReturnRate, years]);

  // Event handlers
  const handleCurrentSavingsChange = (e) =>
    setCurrentSavings(parseFloat(e.target.value) || 0);
  const handleAnnualContributionsChange = (e) =>
    setAnnualContributions(parseFloat(e.target.value) || 0);
  const handleAnnualReturnRateChange = (e) =>
    setAnnualReturnRate(parseFloat(e.target.value) || 0);
  const handleYearsChange = (e) =>
    setYears(parseFloat(e.target.value) || 0);

  return (
    <Layout title="Retirement Calculator" path="retirement-calculator">
      <section className="section-padding" id="retirement-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Retirement Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div className="p-4 border rounded shadow" style={{ border: "1px solid black" }}>
                <h3 className="text-center mb-4">Retirement Calculator</h3>
                <div className="container">
                  {/* Input Fields for Retirement Calculation */}
                  <div className="row gy-3">
                    {/* Current Savings */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="currentSavings" className="form-label">
                        Current Savings (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Current Savings:</strong> The amount of money you have saved so far for retirement."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="currentSavings"
                        placeholder="Enter current savings"
                        value={currentSavings}
                        onChange={handleCurrentSavingsChange}
                      />
                    </div>

                    {/* Annual Contributions */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="annualContributions" className="form-label">
                        Annual Contributions (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Annual Contributions:</strong> The amount of money you plan to save each year towards your retirement."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="annualContributions"
                        placeholder="Enter annual contributions"
                        value={annualContributions}
                        onChange={handleAnnualContributionsChange}
                      />
                    </div>

                    {/* Annual Return Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="annualReturnRate" className="form-label">
                        Annual Return Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Annual Return Rate:</strong> The estimated annual percentage return on your investments."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="annualReturnRate"
                        placeholder="Enter annual return rate"
                        value={annualReturnRate}
                        onChange={handleAnnualReturnRateChange}
                      />
                    </div>

                    {/* Years */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="years" className="form-label">
                        Years
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Years:</strong> The number of years you plan to save for retirement."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="years"
                        placeholder="Enter number of years"
                        value={years}
                        onChange={handleYearsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Future Retirement Savings:{" "}
                        <span className="fw-bold ms-3">
                          ₹{futureRetirementSavings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>
      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Retirement Calculator
                </h3>

                {/* What is a Retirement Calculator? */}
                <h4 className="mt-3 mb-3">What is a Retirement Calculator?</h4>
                <p>
                  A **Retirement Calculator** helps estimate how much money you need to save for your retirement.
                  It takes into account factors like your current age, desired retirement age, current savings, expected returns on investments, and your monthly contributions.
                </p>

                {/* Why Use a Retirement Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Retirement Calculator?</h4>
                <p>
                  The retirement calculator is a valuable tool to help you plan for your future and make informed decisions
                  about how much to save today for a comfortable retirement. It provides a roadmap for saving and investing, ensuring
                  that you meet your retirement goals with sufficient funds.
                </p>

                {/* Benefits of Planning for Retirement Early */}
                <h4 className="mt-4 mb-3">Benefits of Planning for Retirement Early</h4>
                <ul>
                  <li><strong>Financial Security:</strong> Having a clear savings plan helps ensure you have enough money for your post-retirement life.</li>
                  <li><strong>Time is Your Advantage:</strong> The earlier you start saving, the more time your money has to grow thanks to compound interest.</li>
                  <li><strong>Reduced Stress:</strong> Knowing you’re on track to meet your retirement goals reduces financial stress in the future.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Retirement Calculator?</h4>
                <p>
                  A retirement calculator is suitable for anyone who is planning for their future. Whether you’re just starting your career or
                  nearing retirement age, it helps you calculate how much you need to save to ensure a comfortable retirement. This tool
                  is particularly useful for:
                </p>
                <ul>
                  <li><strong>Young Professionals</strong> starting to save for retirement.</li>
                  <li><strong>Mid-Career Individuals</strong> who want to track and adjust their savings plan.</li>
                  <li><strong>Pre-Retirees</strong> who need to see if they are on track to meet their retirement goals.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How much should I save for retirement?</h4>
                <p>
                  The amount you should save depends on various factors such as your current expenses, retirement goals, and lifestyle expectations.
                  The retirement calculator can help determine this based on your personalized data.
                </p>

                <h4 className="mt-4 mb-3">How does compound interest affect retirement savings?</h4>
                <p>
                  Compound interest is the key to growing your retirement savings faster. The earlier you start saving, the more your
                  investments will grow over time due to the compounding effect.
                </p>

                <h4 className="mt-4 mb-3">When should I start saving for retirement?</h4>
                <p>
                  The best time to start saving for retirement is as early as possible. Even small contributions early on can lead to
                  significant savings due to compound interest.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Retirement;
